import { useEffect, useRef, useCallback, type RefObject } from 'react';

interface UsePinchZoomOptions {
  onZoom: (newZoom: number) => void;
  getZoom: () => number;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Intercepts pinch-to-zoom (trackpad & mobile), Ctrl/Cmd +/- on a container element
 * and redirects them to document zoom instead of browser zoom.
 */
export function usePinchZoom(
  containerRef: RefObject<HTMLElement | null>,
  { onZoom, getZoom, min = 0.3, max = 3, step = 0.1 }: UsePinchZoomOptions,
) {
  const focusedRef = useRef(false);
  const touchRef = useRef<{ initialDist: number; initialZoom: number } | null>(null);
  const pinchRef = useRef<{ baseZoom: number; visualScale: number; timer: ReturnType<typeof setTimeout> | null } | null>(null);

  const clamp = useCallback((v: number) => Math.max(min, Math.min(max, v)), [min, max]);

  const applyVisualScale = useCallback((el: HTMLElement, scale: number) => {
    const content = el.firstElementChild as HTMLElement | null;
    if (content) {
      content.style.transformOrigin = 'top center';
      content.style.transform = scale === 1 ? '' : `scale(${scale})`;
    }
  }, []);

  const clearVisualScale = useCallback((el: HTMLElement) => {
    const content = el.firstElementChild as HTMLElement | null;
    if (content) {
      content.style.transform = '';
      content.style.transformOrigin = '';
    }
  }, []);

  const commitZoom = useCallback((el: HTMLElement, newZoom: number) => {
    clearVisualScale(el);
    onZoom(newZoom);
  }, [onZoom, clearVisualScale]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onPointerDownInside = () => { focusedRef.current = true; };
    const onPointerDownOutside = (e: PointerEvent) => {
      if (!el.contains(e.target as Node)) focusedRef.current = false;
    };

    el.addEventListener('pointerdown', onPointerDownInside);
    document.addEventListener('pointerdown', onPointerDownOutside);

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();

      if (!pinchRef.current) {
        pinchRef.current = { baseZoom: getZoom(), visualScale: 1, timer: null };
      }

      const delta = -e.deltaY * 0.005;
      pinchRef.current.visualScale = Math.max(
        min / pinchRef.current.baseZoom,
        Math.min(max / pinchRef.current.baseZoom, pinchRef.current.visualScale + delta),
      );
      applyVisualScale(el, pinchRef.current.visualScale);

      if (pinchRef.current.timer) clearTimeout(pinchRef.current.timer);
      pinchRef.current.timer = setTimeout(() => {
        if (!pinchRef.current) return;
        const finalZoom = clamp(+(pinchRef.current.baseZoom * pinchRef.current.visualScale).toFixed(2));
        pinchRef.current = null;
        commitZoom(el, finalZoom);
      }, 200);
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    const onKeyDown = (e: KeyboardEvent) => {
      if (!focusedRef.current) return;
      if (!(e.ctrlKey || e.metaKey)) return;

      const isPlus = e.key === '+' || e.key === '=';
      const isMinus = e.key === '-';
      if (!isPlus && !isMinus) return;

      e.preventDefault();
      const cur = getZoom();
      const newZoom = clamp(+(cur + (isPlus ? step : -step)).toFixed(2));
      onZoom(newZoom);
    };
    document.addEventListener('keydown', onKeyDown);

    const getTouchDist = (t: TouchList) => {
      const dx = t[0]!.clientX - t[1]!.clientX;
      const dy = t[0]!.clientY - t[1]!.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const initialZoom = getZoom();
        touchRef.current = { initialDist: getTouchDist(e.touches), initialZoom };
        pinchRef.current = { baseZoom: initialZoom, visualScale: 1, timer: null };
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || !touchRef.current || !pinchRef.current) return;
      e.preventDefault();
      const dist = getTouchDist(e.touches);
      const ratio = dist / touchRef.current.initialDist;
      const clampedRatio = Math.max(
        min / pinchRef.current.baseZoom,
        Math.min(max / pinchRef.current.baseZoom, ratio),
      );
      pinchRef.current.visualScale = clampedRatio;
      applyVisualScale(el, clampedRatio);
    };

    const onTouchEnd = () => {
      if (pinchRef.current) {
        const finalZoom = clamp(+(pinchRef.current.baseZoom * pinchRef.current.visualScale).toFixed(2));
        pinchRef.current = null;
        commitZoom(el, finalZoom);
      }
      touchRef.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);

    return () => {
      if (pinchRef.current?.timer) clearTimeout(pinchRef.current.timer);
      clearVisualScale(el);
      el.removeEventListener('pointerdown', onPointerDownInside);
      document.removeEventListener('pointerdown', onPointerDownOutside);
      el.removeEventListener('wheel', onWheel);
      document.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [containerRef, onZoom, getZoom, clamp, step, applyVisualScale, clearVisualScale, commitZoom, min, max]);
}
